import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, FormControlLabel, Icon, IconButton, TextField, TextFieldProps, Tooltip, keyframes } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import ProgressBar from '../controls/ProgressBar';

import { plannenService } from '../../services/plannen.service';

import 'dayjs/locale/nl'; // Import Dutch locale
import dayjs, { Dayjs } from 'dayjs';
// Set default locale globally
dayjs.locale('nl');

const MIN_DATE = dayjs('1970-01-01');
const MAX_DATE = dayjs('2500-12-31');

interface FilterPlannenProps {
  onSearch: (query: string, from: string, to: string, matchExactRange: boolean, filters: Record<string, any[]>) => void;
  onChange: (key: string, value: any, isDelete?: boolean) => void;
  filterData?: any;
  selectedSearch: string;
  selectedStartDate: string;
  selectedEndDate: string;
  matchExactRange: boolean;
  selectedFilters: Record<string, any[]>;
}

const FilterPlannen: React.FC<FilterPlannenProps> = ({
  onSearch,
  onChange,
  filterData,
  selectedSearch,
  selectedStartDate,
  selectedEndDate,
  matchExactRange,
  selectedFilters
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filterOptionOpen, setFilterOptionOpen] = useState(false);
  const [errorStartDate, setErrorStartDate] = useState<string | null>(null);
  const [errorEndDate, setErrorEndDate] = useState<string | null>(null);

  const [disciplines, setDisciplines] = useState<any[]>([]);
  const [statusses, setStatusses] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [organisaties, setOrganisaties] = useState<any[]>([]);
  const [beoordelingStatusses, setBeoordeling] = useState<any[]>([]);

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  // useEffect(() => {
  //   // Ensure the fetch happens only once
  //   if (!hasFetchedData.current) {
  //     setFilterData(filterData);
  //     hasFetchedData.current = true; // Mark as fetched
  //   }
  // }, []); // Empty dependency array ensures this runs once when the component mounts

  useEffect(() => {
    // Ensure the fetch happens only once
    if (!hasFetchedData.current && filterData && Object.keys(filterData).length > 0) {
      setFilterData(filterData);
      hasFetchedData.current = true; // Mark as fetched
    }
  }, [filterData]); // Empty dependency array ensures this runs once when the component mounts

  const setFilterData = (data: any) => {
    const filters = data;
    if (filters.discipline) {
      setDisciplines(filters.discipline);
    }

    if (filters.planStatus) {
      setStatusses(filters.planStatus);
    }

    if (filters.planType) {
      setTypes(filters.planType);
    }

    if (filters.infplBeoordeling) {
      setBeoordeling(filters.infplBeoordeling);
    }

    if (filters.organizations) {
      setOrganisaties(filters.organizations);
    }
  };
  
  const handleSearch = async (value: any) => {
    // console.log('handleSearch:', selectedSearch, selectedStartDate, selectedEndDate, selectedFilters);
    // // Odata filters // // https://learn.microsoft.com/en-us/azure/search/search-query-odata-filter // https://learn.microsoft.com/en-us/azure/search/search-query-odata-comparison-operators
    // setSelectedSearch(value);
    onSearch(selectedSearch || '', selectedStartDate, selectedEndDate, matchExactRange, selectedFilters); // Trigger search update
    setFilterOptionOpen(false);
  };

  const onClickSearch = async (value: any) => {
    handleSearch(value);
  };

  const onChangeSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    //console.log("onChangeSearchInput:", value);
    onChange('search', value);

    // setSelectedSearch(searchValue);
  };

  const onChangeDate = (key: string, value: any) => {
    onChange(key, value);
  };

  const onClickCheckbox = async (key: string, value: any) => {
    onChange(key, value);
  };

  const isChecked = (key: string, object: any) => {
    switch (key) {
      case 'status':
        return selectedFilters.status.includes(object.id);
      case 'discipline':
        return selectedFilters.discipline.includes(object.id);
      case 'type':
        return selectedFilters.type.includes(object.id);
      case 'organisatie':
        return selectedFilters.organisatie.includes(object.id);
      case 'beoordeling':
        return selectedFilters.beoordeling.includes(object.id);
      default:
        return false;
    }
  };

  return (
    <>
      <div className="filter-advanced">
        
        <div className="search">
          {/* <label>Zoeken</label>
          <br/> */}
          <TextField
            fullWidth
            // sx={{ mt: 2 }}
            id="search"
            label="Zoeken in plannen" 
            variant="filled"
            placeholder="Zoeken op nummer, naam, status, beoordeling, type, discipline en organisatie"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                const inputValue = (event.target as HTMLInputElement).value;
                handleSearch(inputValue); // Call your function with the appropriate argument here
              }
            }}
            value={selectedSearch ?? ''}
            onChange={onChangeSearchInput}
            // error={!!validationErrors.displayName}
          />
          {!filterOptionOpen && (
            <Tooltip title="Open filter om uitgebreid te zoeken in de plannen">
              <IconButton className='btn-filter-toggle' size="large" onClick={() => setFilterOptionOpen(!filterOptionOpen)}>
                <Icon>filter_list</Icon>
              </IconButton>
            </Tooltip>
          )}

          {filterOptionOpen && (
            <Tooltip title="Sluit het filter">
              <IconButton className='btn-filter-toggle' size="large" onClick={() => setFilterOptionOpen(!filterOptionOpen)}>
                <Icon>filter_list_off</Icon>
              </IconButton>
            </Tooltip>
          )}
          

          {selectedSearch && (
            <IconButton className='btn-search-clear' size="large" onClick={() => onChange('search', '')}>
              <Icon>clear</Icon>
            </IconButton>
          )}

        </div>

        <div className={`filter-options ${filterOptionOpen ? "active" : ""}`}>
          <Tooltip title="Verwijder filters">
            <IconButton className='btn-filter-clear' size="large" onClick={() => onChange('all', '', true)}>
              <Icon>delete</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Sluit filter">
            <IconButton className='btn-filter-close' size="large" onClick={() => setFilterOptionOpen(false)}>
              <Icon>close</Icon>
            </IconButton>
          </Tooltip>

          <div className="container">
            <div className="row">
              <div className="col-xxl-12 col-xl-12 col-lg-12">
                <h5>Uitgebreid zoeken in alle plannen</h5>
              </div>
              <div className="col-xxl-12 col-xl-12 col-lg-12">
                <div className="search">
                  <TextField
                    fullWidth
                    id="search"
                    label="Zoeken in plannen" 
                    variant="filled"
                    placeholder="Zoeken op nummer, naam, status, type, discipline en organisatie"
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        const inputValue = (event.target as HTMLInputElement).value;
                        handleSearch(inputValue); // Call your function with the appropriate argument here
                      }
                    }}
                    value={selectedSearch ?? ''}
                    onChange={onChangeSearchInput}
                  />

                  { selectedSearch && (
                    <IconButton className='btn-search-clear' size="large" onClick={() => onChange('search', '')}>
                      <Icon>clear</Icon>
                    </IconButton>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="scrollable-area">
            <div className="container">
              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-lg-12">
                  <h5>Start en einddatum</h5>
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-12">
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Van"
                    value={selectedStartDate ? dayjs(selectedStartDate) : null}
                    onChange={(date) => {
                      const iso = date?.format('YYYY-MM-DD');
                      onChangeDate('from', iso);
                    }}
                    onError={(newError) => {
                      setErrorStartDate(newError)
                    }}
                    format="DD/MM/YYYY"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        error: !!error,
                        helperText:
                          error === 'minDate'
                            ? 'Datum mag niet voor 01/01/1970 zijn.'
                            : errorStartDate === 'maxDate'
                            ? 'Datum mag niet na 31/12/2500 zijn.'
                            : errorStartDate === 'invalidDate'
                            ? 'Ongeldige datum.'
                            : '',
                        FormHelperTextProps: {
                          sx: {
                            color: 'error.main',
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="col-xxl-6 col-xl-6 col-lg-12">
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Tot en met"
                    value={selectedEndDate ? dayjs(selectedEndDate) : null} // Convert selectedEndDate to a Dayjs object
                    onChange={(date) => {
                      const iso = date?.format('YYYY-MM-DD');
                      onChangeDate('to', iso);
                    }}
                    onError={(newError) => {
                      setErrorEndDate(newError)
                    }}
                    format="DD/MM/YYYY"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    slotProps={{
                      textField: {
                        variant: 'filled',
                        error: !!error,
                        helperText:
                          error === 'minDate'
                            ? 'Datum mag niet voor 01/01/1970 zijn.'
                            : errorEndDate === 'maxDate'
                            ? 'Datum mag niet na 31/12/2500 zijn.'
                            : errorEndDate === 'invalidDate'
                            ? 'Ongeldige datum.'
                            : '',
                        FormHelperTextProps: {
                          sx: {
                            color: 'error.main',
                          },
                        },
                        className: error ? 'datepicker-error' : '', // ✅ custom class here
                      },
                    }}
                  />
                </div>
                <div className="col-xxl-12 col-xl-12 col-lg-12">
                  <FormControlLabel
                    label="Exacte datumbereik. Plan moet volledig binnen de datumbereik vallen"
                    control={
                      <Checkbox
                        checked={matchExactRange}
                        onChange={() => onChangeDate('matchExactRange', !matchExactRange)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                  />
                </div>
              </div>

              { statusses && statusses.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Status</h5>
                  </div>

                  { statusses.map((status, index) => (
                    <div key={status.id} className="col-xxl-3 col-xl-4 col-lg-6">
                      <FormControlLabel
                        label={status.name}
                        control={
                          <Checkbox
                            checked={isChecked('status', status)}
                            onChange={() => onClickCheckbox('status', status.id)}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                      />
                    </div>
                  ))}

                </div>
              )}

              { beoordelingStatusses && beoordelingStatusses.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Beoordeling</h5>
                  </div>

                  { beoordelingStatusses.map((status, index) => (
                    <div key={status.id} className="col-xxl-3 col-xl-4 col-lg-6">
                      <FormControlLabel
                        label={status.name}
                        control={
                          <Checkbox
                            checked={isChecked('beoordeling', status)}
                            onChange={() => onClickCheckbox('beoordeling', status.id)}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                      />
                    </div>
                  ))}

                </div>
              )}

              { disciplines && disciplines.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Discipline</h5>
                  </div>

                  { disciplines.map((discipline, index) => (
                    <div key={discipline.id} className="col-xxl-3 col-xl-4 col-lg-6">
                      <FormControlLabel
                        label={discipline.name}
                        control={
                          <Checkbox
                            checked={isChecked('discipline', discipline)}
                            onChange={() => onClickCheckbox('discipline', discipline.id)}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                      />
                    </div>
                  ))}

                </div>
              )}

              { types && types.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Type</h5>
                  </div>

                  { types.map((type, index) => (
                    <div key={type.id} className="col-xxl-3 col-xl-4 col-lg-6">
                      <FormControlLabel
                        label={type.name}
                        control={
                          <Checkbox
                            checked={isChecked('type', type)}
                            onChange={() => onClickCheckbox('type', type.id)}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                      />
                    </div>
                  ))}

                </div>
              )}

              { organisaties && organisaties.length > 0 && (
                <div className="row">
                  <div className="col-xxl-12 col-xl-12 col-lg-12">
                    <h5>Organisatie</h5>
                  </div>

                  { organisaties.map((organisatie, index) => (
                    <div key={organisatie.id} className="col-xxl-3 col-xl-4 col-lg-6">
                      <FormControlLabel
                        label={organisatie.name}
                        control={
                          <Checkbox
                            checked={isChecked('organisatie', organisatie)}
                            onChange={() => onClickCheckbox('organisatie', organisatie.id)}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                      />
                    </div>
                  ))}

                </div>
              )}


            </div>
          </div>

          <Button className="btn-filter-zoeken" variant="contained" color="secondary" onClick={onClickSearch}>
            <Icon>search</Icon>
            <span>Zoeken</span>
          </Button>

        </div>
      </div>

      { loading && (
        <ProgressBar />
      )}

    </>
  );
};

export default FilterPlannen;